import { useState, useEffect, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import moment from "moment";
import { unregisterCurrentMobilePushDevice } from "../../utils/mobilePushRuntime";
import {
  requestMessagesRefresh,
  requestTicketsListRefresh
} from "../../utils/ticketRealtime";

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [socket, setSocket] = useState(null);
  const activeSocketRef = useRef(null);
  const realtimeResumeAtRef = useRef(0);
  const lastVisibilityHiddenAtRef = useRef(0);
  const userSocketListenerRef = useRef({
    socket: null,
    eventName: "",
    handler: null
  });

  const detachUserSocketListener = useCallback(() => {
    const currentListener = userSocketListenerRef.current;

    if (
      currentListener?.socket &&
      currentListener?.eventName &&
      currentListener?.handler &&
      typeof currentListener.socket.off === "function"
    ) {
      currentListener.socket.off(
        currentListener.eventName,
        currentListener.handler
      );
    }

    userSocketListenerRef.current = {
      socket: null,
      eventName: "",
      handler: null
    };
  }, []);

  const syncRealtimeState = useCallback((reason = "manual") => {
    if (!user?.id || !user?.companyId) {
      return;
    }

    const now = Date.now();
    if (now - Number(realtimeResumeAtRef.current || 0) < 2500) {
      return;
    }

    realtimeResumeAtRef.current = now;

    const socketInstance = activeSocketRef.current || socket;
    if (socketInstance) {
      if (typeof socketInstance.forceReconnect === "function") {
        socketInstance.forceReconnect();
      } else if (typeof socketInstance.connect === "function") {
        socketInstance.connect();
      }
    }

    requestTicketsListRefresh({
      reason: `auth-${reason}`
    });
    requestMessagesRefresh({
      reason: `auth-${reason}`
    });
  }, [socket, user?.companyId, user?.id]);

  // Re-login silencioso com credencial salva (localStorage['agilzap_cred']) —
  // usado tanto no carregamento inicial quanto quando um 401 acontece no meio
  // da sessão (ver interceptor de resposta abaixo). Existe pra apps que fazem
  // SSO automático (ex: EquipeChat Visita semeia essa credencial só UMA VEZ,
  // antes de carregar o iframe de /cadastro pela primeira vez) — sem essa
  // segunda tentativa aqui, se o token expirasse ou um 401 acontecesse depois
  // disso (ex: durante um restart do backend), a tela de login ficava presa
  // pra sempre dentro do iframe, já que nada mais tentava logar de novo sozinho.
  const attemptCredentialAutoLogin = async () => {
    const credRaw = localStorage.getItem("agilzap_cred");
    if (!credRaw) return false;
    try {
      // 12/09/2026 — quem grava isso (app EquipeChat Visita, ver
      // seedCadastroSso no index.html) passou a usar base64 seguro pra
      // acento (senha com ç/ã/é etc não sobrevivia num btoa() puro do lado
      // de lá - jogava exceção e nada era gravado, essa tela de login
      // aparecia sem credencial nenhuma pra tentar). decodeURIComponent(escape(atob(...)))
      // é o reverso disso - com fallback pro atob() puro de antes, pra não
      // quebrar credencial ASCII já gravada do jeito antigo.
      let credJson;
      try { credJson = decodeURIComponent(escape(atob(credRaw))); }
      catch (e) { credJson = atob(credRaw); }
      const cred = JSON.parse(credJson);
      const { data: loginData } = await api.post("/auth/login", cred);
      if (loginData?.token) {
        localStorage.setItem("token", JSON.stringify(loginData.token));
        api.defaults.headers.Authorization = `Bearer ${loginData.token}`;
        setIsAuth(true);
        setUser(loginData.user || loginData);
        return true;
      }
    } catch (e) {
      // credencial inválida ou expirada — quem chamou decide o que fazer
    }
    return false;
  };

  // Interceptors do API (mantém como estava)
  api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers["Authorization"] = `Bearer ${JSON.parse(token)}`;
        setIsAuth(true);
      }
      return config;
    },
    (error) => {
      Promise.reject(error);
    }
  );

  api.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      if (error?.response?.status === 403 && !originalRequest._retry) {
        originalRequest._retry = true;

        const { data } = await api.post("/auth/refresh_token");
        if (data) {
          localStorage.setItem("token", JSON.stringify(data.token));
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
        }
        return api(originalRequest);
      }
      if (error?.response?.status === 401) {
        const requestUrl = String(originalRequest?.url || "");
        const isAuthRequest =
          requestUrl.includes("/auth/login") ||
          requestUrl.includes("/auth/refresh_token");
        if (!isAuthRequest && !originalRequest._ssoRetry) {
          originalRequest._ssoRetry = true;
          const loggedInAgain = await attemptCredentialAutoLogin();
          if (loggedInAgain) {
            return api(originalRequest);
          }
        }
        localStorage.removeItem("token");
        api.defaults.headers.Authorization = undefined;
        setIsAuth(false);
      }
      return Promise.reject(error);
    }
  );

  // Effect para inicialização do token
  useEffect(() => {
    const token = localStorage.getItem("token");
    (async () => {
      if (token) {
        try {
          const { data } = await api.post("/auth/refresh_token");
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
          setIsAuth(true);
          setUser(data.user || data);
          setLoading(false);
          return;
        } catch (err) {
          // Sessão expirada → tenta re-login silencioso com credenciais salvas
          if (err?.response?.status !== 401 && err?.response?.status !== 403) {
            toastError(err);
          }
        }
      }
      // Tenta re-login automático com credenciais salvas (ex: web clip / PWA)
      await attemptCredentialAutoLogin();
      setLoading(false);
    })();
  }, []);

  // Effect para configuração do socket
  useEffect(() => {
    if (!user?.id || !user?.companyId) {
      return undefined;
    }

    console.log("Configurando socket para user", user.id, "company", user.companyId);
    detachUserSocketListener();

    const socketInstance = socketConnection({
      user: {
        companyId: user.companyId,
        id: user.id
      }
    });

    activeSocketRef.current = socketInstance;
    setSocket(socketInstance);

    const eventName = `company-${user.companyId}-user`;
    const handleUserUpdate = (data) => {
      if (data?.action === "update" && data?.user?.id === user.id) {
        setUser(data.user);
      }
    };

    if (socketInstance && typeof socketInstance.on === "function") {
      socketInstance.on(eventName, handleUserUpdate);
      userSocketListenerRef.current = {
        socket: socketInstance,
        eventName,
        handler: handleUserUpdate
      };
    }

    return () => {
      if (
        socketInstance &&
        typeof socketInstance.off === "function" &&
        eventName
      ) {
        socketInstance.off(eventName, handleUserUpdate);
      }

      if (
        userSocketListenerRef.current.socket === socketInstance &&
        userSocketListenerRef.current.eventName === eventName &&
        userSocketListenerRef.current.handler === handleUserUpdate
      ) {
        userSocketListenerRef.current = {
          socket: null,
          eventName: "",
          handler: null
        };
      }

      if (activeSocketRef.current === socketInstance) {
        activeSocketRef.current = null;
      }
    };
  }, [detachUserSocketListener, user?.companyId, user?.id]);

  useEffect(() => {
    if (!isAuth || !user?.id || !user?.companyId || typeof window === "undefined") {
      return undefined;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        lastVisibilityHiddenAtRef.current = Date.now();
        return;
      }

      const hiddenForMs =
        Date.now() - Number(lastVisibilityHiddenAtRef.current || 0);
      if (hiddenForMs >= 400) {
        syncRealtimeState("visibility");
      }
    };

    const handleWindowFocus = () => {
      syncRealtimeState("focus");
    };

    const handleWindowOnline = () => {
      syncRealtimeState("online");
    };

    const handlePageShow = (event) => {
      if (event?.persisted) {
        syncRealtimeState("pageshow");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    document.addEventListener("resume", handleWindowFocus);
    window.addEventListener("focus", handleWindowFocus);
    window.addEventListener("online", handleWindowOnline);
    window.addEventListener("pageshow", handlePageShow);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      document.removeEventListener("resume", handleWindowFocus);
      window.removeEventListener("focus", handleWindowFocus);
      window.removeEventListener("online", handleWindowOnline);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, [isAuth, syncRealtimeState, user?.companyId, user?.id]);

  // Effect para buscar dados do usuário atual
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data.user || data);
      } catch (err) {
        console.log("Erro ao buscar usuário atual:", err);
      }
    };
    
    if (isAuth) {
      fetchCurrentUser();
    }
  }, [isAuth]);

  const handleLogin = async (userData) => {
    setLoading(true);

    try {
      const { data } = await api.post("/auth/login", userData);
      const {
        user: { company },
      } = data;

      // Lógica de configurações da empresa (mantém como estava)
      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null);
        }
      }

      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "sendSignMessage"
        );

        const signEnable = setting.value === "enable";

        if (setting && setting.value === "enabled") {
          localStorage.setItem("sendSignMessage", signEnable);
        }
      }
      
      localStorage.setItem("profileImage", data.user.profileImage);

      moment.locale("pt-br");
      let dueDate;
      if (data.user.company.id === 1) {
        dueDate = "2999-12-31T00:00:00.000Z";
      } else {
        dueDate = data.user.company.dueDate;
      }
      
      const vencimento = moment(dueDate).format("DD/MM/yyyy");

      // Comparar apenas as datas (sem horas) para permitir acesso até 23h59 do dia do vencimento
      const hojeInicio = moment().startOf('day');
      const vencimentoInicio = moment(dueDate).startOf('day');
      
      var diff = vencimentoInicio.diff(hojeInicio, 'days');
      var before = hojeInicio.isSameOrBefore(vencimentoInicio, 'day');
      var dias = diff;

      if (before === true) {
        localStorage.setItem("token", JSON.stringify(data.token));
        localStorage.setItem("companyDueDate", vencimento);
        // Salva credenciais para re-login automático no web clip (PWA)
        try { localStorage.setItem("agilzap_cred", btoa(JSON.stringify(userData))); } catch {}
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setUser(data.user || data);
        setIsAuth(true);
        toast.success(i18n.t("auth.toasts.success"));

        if (Math.round(dias) < 5) {
          toast.warn(
            `Sua assinatura vence em ${Math.round(dias)} ${
              Math.round(dias) === 1 ? "dia" : "dias"
            } `
          );
        }

        const fromLocation = history.location?.state?.from;
        const fromPath = fromLocation?.pathname;
        // Preserva a query string (ex: ?openRegistration=1&contactId=...) — sem isso,
        // um link direto pra ficha de um cliente perdia qual cliente era depois do login.
        const fromFull = fromPath ? fromPath + (fromLocation?.search || "") : null;
        // "/" não é destino válido — cai no fallback
        let postLoginTarget = (fromPath && fromPath !== "/") ? fromFull : null;
        if (!postLoginTarget) {
          try {
            postLoginTarget = localStorage.getItem("agilzap_home");
            if (!postLoginTarget) {
              // Web clip / standalone: vai direto para cadastro
              const isStandalone = (window.navigator.standalone === true) ||
                window.matchMedia("(display-mode: standalone)").matches;
              postLoginTarget = isStandalone ? "/cadastro" : "/tickets";
            }
          } catch(e) { postLoginTarget = "/tickets"; }
        }
        history.push(postLoginTarget);
        setLoading(false);
      } else {
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setIsAuth(true);
        toastError(`Opss! Sua assinatura venceu ${vencimento}.
Entre em contato com o Suporte para mais informações! `);
        history.push("/financeiro-aberto");
        setLoading(false);
      }
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      try {
        await unregisterCurrentMobilePushDevice(api);
      } catch (mobilePushError) {
        console.error(
          "[EquipeChat Teste] Falha ao desvincular notificacoes no logout",
          mobilePushError
        );
      }

      // Limpar socket antes do logout
      detachUserSocketListener();

      const socketInstance = activeSocketRef.current || socket;
      if (socketInstance && typeof socketInstance.disconnect === "function") {
        socketInstance.disconnect();
      }

      activeSocketRef.current = null;

      await api.delete("/auth/logout");
      setIsAuth(false);
      setUser({});
      setSocket(null);
      localStorage.removeItem("token");
      localStorage.removeItem("cshow");
      // Sem isso, a credencial salva pro re-login automático (ver
      // attemptCredentialAutoLogin/agilzap_cred, pensada pra PWA/web clip)
      // ficava viva depois do logout - a tela de login relogava sozinha com o
      // usuário antigo antes da pessoa conseguir digitar outro usuário/senha.
      localStorage.removeItem("agilzap_cred");
      api.defaults.headers.Authorization = undefined;
      setLoading(false);
      history.push("/login");
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      console.log(data);
      return data;
    } catch (_) {
      return null;
    }
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
    socket,
  };
};

export default useAuth;
